import { useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Building2, Check } from "lucide-react";
import { toast } from "sonner";

export default function EnterpriseContactDialog({ triggerClassName = "", triggerLabel = "Talk to sales" }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await apiClient.post("/leads", form);
      setSent(true);
      toast.success("Thanks — we'll be in touch shortly! 🚢");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setSent(false); setForm({ name: "", email: "", company: "", message: "" }); }, 200);
  };

  return (
    <>
      <button type="button" data-testid="enterprise-cta" onClick={() => setOpen(true)} className={triggerClassName}>
        {triggerLabel}
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="border-2 border-ink rounded-3xl shadow-nb-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange" /> Talk to sales
            </DialogTitle>
            <DialogDescription className="font-600 text-ink/60">
              Enterprise — SSO, AI feedback intelligence, audit logs, SLAs and custom contracts. Tell us a bit and we'll reach out.
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="py-8 text-center" data-testid="enterprise-sent">
              <div className="h-14 w-14 bg-grass border-2 border-ink rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-nb-sm">
                <Check className="h-7 w-7 text-white" />
              </div>
              <p className="font-display font-700 text-xl mb-1">Message sent!</p>
              <p className="text-sm text-ink/60 font-600 mb-6">Our team will get back to you at {form.email}.</p>
              <button onClick={close} className="nb-btn bg-ink text-cream px-5 py-2.5 text-sm">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="enterprise-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-name" className="nb-input" placeholder="Your name" />
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="lead-company" className="nb-input" placeholder="Company" />
              </div>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="lead-email" className="nb-input w-full" placeholder="Work email" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="lead-message" rows={3} className="nb-input w-full resize-none" placeholder="What are you looking to do? (optional)" />
              <button type="submit" disabled={sending} data-testid="lead-submit"
                className="nb-btn bg-orange text-white w-full py-3 text-sm disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
